import axios from "axios";
import { useState } from "react";
import { toast } from "react-toastify";
import { GRAAL_API_URL } from "../../../../../config";
import tools from "../../../../../helpers/tools";
import { graalApiAxiosInstance } from "../../../../../middlewares/useApiAuthMiddleware";
import DocumentsCompacts from "../../../../common/CarteLead/DocumentsCompacts";
import Loader from "../../../../common/Loader";
import DropZone from "../../../ClientListPage/ClientList/ClientItem/CompanyList/CompanyItem/BottomPanel/BPDocuments/BPDocumentItem/DropZone";

export const DOCUMENT_LABELS = {
  DG: "Dispositions générales",
  IPID: "IPID",
  DER_VIERGE: "DER vierge",
};

// une seule requête produits-auto pour toutes les lignes de leads
let autoProductsCache = null;
const fetchAutoProducts = () => {
  if (autoProductsCache) return Promise.resolve(autoProductsCache);
  return graalApiAxiosInstance
    .get(`${GRAAL_API_URL}new-leads/produits-auto`)
    .then((res) => {
      autoProductsCache = res.data.produits || [];
      return autoProductsCache;
    });
};

const groupProductsByCompany = (produits) => {
  const groupes = new Map();

  produits.forEach((produit) => {
    const compagnie = produit.compagnie || "Sans compagnie";
    if (!groupes.has(compagnie)) groupes.set(compagnie, []);
    groupes.get(compagnie).push(produit);
  });

  return groupes;
};

const LeadAttachedDocument = ({
  lead,
  documentType = "",
  canUpload,
  uploadUrl = "",
  onUploadSuccess,
  canNotify = false,
  compact = false,
  emptyLabel = "aucun document",
}) => {
  const uploadURL = `${GRAAL_API_URL}${uploadUrl}` || `${GRAAL_API_URL}s`;
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notifyClient, setNotifyClient] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [autoProducts, setAutoProducts] = useState(null);
  const [docsWarning, setDocsWarning] = useState(null);

  const onDropFiles = (files) => {
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

    if (files.length > 0 && allowedTypes.indexOf(files[0].type) != -1) {
      if (canNotify) {
        setPendingFiles(files);
        setNotifyClient(false);
        setSelectedProduct("");
        setDocsWarning(null);
        setShowConfirmModal(true);
        fetchAutoProducts()
          .then(setAutoProducts)
          .catch(() => setAutoProducts([]));
      } else {
        let data = new FormData();
        data.append("file", files[0]);
        data.append("type", documentType);
        uploadFile(data);
      }
    } else {
      toast.error(
        "Le fichier choisi est incorrect. Les formats acceptés sont jpeg, png et pdf.",
      );
    }
  };

  const onConfirmUpload = () => {
    setShowConfirmModal(false);
    if (pendingFiles) {
      let data = new FormData();
      data.append("file", pendingFiles[0]);
      data.append("type", documentType);
      data.append("notifier", notifyClient ? "1" : "0");
      if (notifyClient && selectedProduct) {
        data.append("produit", selectedProduct);
      }
      uploadFile(data);
      setPendingFiles(null);
      setSelectedProduct("");
      setDocsWarning(null);
    }
  };

  const onCancelUpload = () => {
    setShowConfirmModal(false);
    setPendingFiles(null);
    setSelectedProduct("");
    setDocsWarning(null);
  };

  // avertissement de confort, jamais bloquant (spec PER-2393)
  const onSelectProduct = (e) => {
    const produit = e.target.value;
    setSelectedProduct(produit);
    setDocsWarning(null);
    if (!produit) return;
    graalApiAxiosInstance
      .get(`${GRAAL_API_URL}new-leads/produits-auto/etat-documents`, {
        params: { produit },
      })
      .then((res) => {
        if (!res.data.produitTrouve) {
          setDocsWarning({ type: "unknown" });
        } else if (res.data.manquants?.length) {
          setDocsWarning({ type: "missing", manquants: res.data.manquants });
        }
      })
      .catch(() => setDocsWarning({ type: "unknown" }));
  };
  function uploadFile(data) {
    setIsUploading(true);
    graalApiAxiosInstance
      .post(uploadURL, data, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          let percentCompleted = Math.floor(
            (progressEvent.loaded * 100) / progressEvent.total,
          );

          setProgress(percentCompleted);
          // do whatever you like with the percentage complete
          // maybe dispatch an action that will update a progress bar or something
        },
      })
      .then((res) => {
        onDocumentUploaded(res.data);
      })
      .catch((err) => {
        const validationErrors =
          err.response?.status === 422 && err.response.data?.errors;
        if (validationErrors) {
          toast.error(Object.values(validationErrors).flat().join(" "));
        } else if (err.response && err.response.data) {
          toast.error("L'envoi du document a échoué. Veuillez réessayer.");
        }
      })
      .then(() => {
        setIsUploading(false);
      });
  }

  const onDocumentUploaded = (data) => {
    // En compact, la liste rafraîchie montre le document : un toast suffit,
    // plutôt qu'une ligne qui resterait affichée sur la carte.
    if (compact) toast.success("Document déposé.");
    else setIsUploaded(true);
    if (onUploadSuccess) onUploadSuccess(data);
  };

  return (
    <div>
      {isUploaded && (
        <div className="text-info">
          <i className="fa fa-check mr-2" />
          Document envoyé avec succès
        </div>
      )}
      {compact ? (
        <DocumentsCompacts documents={documentsCompactsDe(lead, documentType)} emptyLabel={emptyLabel} />
      ) : (
        <ExistingDocument lead={lead} documentType={documentType} />
      )}
      {canUpload && (
        <DropZone onDropFiles={onDropFiles} isUploading={isUploading} compact={compact} label="Déposer un devis" />
      )}

      {showConfirmModal && (
        <div
          className="position-fixed d-flex align-items-center justify-content-center"
          style={{
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 99999,
          }}
          onClick={onCancelUpload}
        >
          <div
            className="bg-white p-4 animated zoomIn faster"
            style={{
              borderRadius: 8,
              maxWidth: 450,
              width: "90%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="mb-3">
              <i className="fa fa-upload mr-2" />
              Envoi du document
            </h5>
            <p className="mb-3" style={{ fontSize: 14, color: "#555" }}>
              Fichier : <strong>{pendingFiles?.[0]?.name}</strong>
            </p>
            <label
              className="d-flex align-items-center mb-3"
              style={{ cursor: "pointer", gap: 8 }}
            >
              <input
                type="checkbox"
                checked={notifyClient}
                onChange={(e) => {
                  setNotifyClient(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedProduct("");
                    setDocsWarning(null);
                  }
                }}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />
              <span style={{ fontSize: 14 }}>
                Notifier le client par e-mail
              </span>
            </label>
            {notifyClient && (
              <div className="mb-3">
                <label
                  className="mb-1"
                  style={{ fontSize: 13, fontWeight: 500 }}
                >
                  Produit concerné
                </label>
                <select
                  className="form-control form-control-sm"
                  value={selectedProduct}
                  onChange={onSelectProduct}
                  disabled={autoProducts === null}
                >
                  <option value="" disabled>
                    {autoProducts === null
                      ? "Chargement des produits…"
                      : "-- Choisir un produit --"}
                  </option>
                  {Array.from(
                    groupProductsByCompany(autoProducts || []),
                    ([compagnie, items]) => (
                      <optgroup key={compagnie} label={compagnie}>
                        {items.map((produit) => (
                          <option key={produit.nom} value={produit.nom}>
                            {produit.libelle === produit.nom
                              ? produit.nom
                              : `${produit.libelle} - ${produit.nom}`}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
                </select>
                {docsWarning && (
                  <div
                    className="alert alert-warning py-2 px-3 mt-2 mb-0"
                    style={{ fontSize: 13 }}
                  >
                    <i className="fa fa-exclamation-triangle mr-2" />
                    {docsWarning.type === "missing"
                      ? `Les documents suivants ne seront pas joints à l'email : ${docsWarning.manquants
                          .map((m) => DOCUMENT_LABELS[m] || m)
                          .join(", ")}.`
                      : "L'état des documents du produit n'a pas pu être vérifié : certains documents pourraient ne pas être joints."}{" "}
                    L'envoi reste possible.
                  </div>
                )}
              </div>
            )}
            <div className="d-flex justify-content-end" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={onCancelUpload}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={onConfirmUpload}
                disabled={notifyClient && !selectedProduct}
              >
                <i className="fa fa-check mr-1" />
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadAttachedDocument;

const lireBlob = (docRef) =>
  axios({ url: docRef.file, method: "GET", responseType: "blob" }).then((res) => {
    const contentType = res.headers["content-type"] || "application/octet-stream";
    return new Blob([res.data], { type: contentType });
  });

const journaliserErreur = (err) => {
  if (err.response && err.response.data) {
    console.error(err.response.data.other);
  }
};

const telechargerDocument = (docRef) =>
  lireBlob(docRef)
    .then((blob) => {
      const link = document.createElement("a");
      // create a blobURI pointing to our Blob
      link.href = URL.createObjectURL(blob);
      link.download = docRef.filename || docRef.name;
      // some browser needs the anchor to be in the doc
      document.body.append(link);
      link.click();
      link.remove();
      // in case the Blob uses a lot of memory
      setTimeout(() => URL.revokeObjectURL(link.href), 7000);
    })
    .catch(journaliserErreur);

const ouvrirDocument = (docRef) =>
  lireBlob(docRef)
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    })
    .catch((err) => {
      journaliserErreur(err);
      toast.error("Impossible de prévisualiser le document.");
    });

const ExistingDocument = ({ lead, documentType }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  if (!lead.documents || !Array.isArray(lead.documents)) {
    return <i className="text-danger">aucun document</i>;
  }
  const docRef = lead.documents
    .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
    .find((d) => d.type === documentType);
  if (!docRef) {
    return <i className="text-danger">aucun document</i>;
  }

  const downloadFileFromLocal = () => {
    setIsDownloading(true);
    telechargerDocument(docRef).then(() => setIsDownloading(false));
  };

  const previewFile = () => {
    setIsPreviewing(true);
    ouvrirDocument(docRef).then(() => setIsPreviewing(false));
  };

  return (
    <div className="mb-2">
      <div
        className="d-flex align-items-center"
        style={{ fontSize: 12, gap: 5 }}
      >
        <div
          className="badge badge-success rounded-circle d-center"
          style={{ width: 16, height: 16 }}
        >
          <i className="fa fa-check text-white" />
        </div>
        envoyé le {tools.formatDate(docRef.uploadedAt, "dd/MM/yyyy HH:mm")}
        {docRef.origine === "agent" && (
          <span
            className="badge badge-info"
            title="Devis créé par l'agent de tarification"
          >
            <i className="fa fa-magic mr-1" />
            Généré par l'IA le{" "}
            {tools.formatDate(
              docRef.genereLe || docRef.uploadedAt,
              "dd/MM/yyyy HH:mm",
            )}
          </span>
        )}
      </div>
      <div className="d-flex align-items-center" style={{ gap: 5 }}>
        <button
          className="btn btn-link border-0 px-0 text-left font-weight-normal py-2"
          style={{ fontSize: 12 }}
          onClick={previewFile}
          title="Prévisualiser"
        >
          {isPreviewing ? <Loader /> : <i className="fa fa-eye mr-1"></i>}
        </button>
        <button
          className="btn btn-link border-0 px-0 text-left font-weight-normal py-2"
          style={{ fontSize: 12 }}
          onClick={downloadFileFromLocal}
          title="Télécharger"
        >
          {isDownloading ? <Loader /> : <i className="fa fa-download mr-1"></i>}
        </button>
        <span
          style={{
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {docRef.name}
        </span>
      </div>
    </div>
  );
};

// Rendu compact (carte lead auto) : les documents graal du type, au format du
// kit commun (DocumentsCompacts), avec l'aperçu et le téléchargement par blob.
const documentsCompactsDe = (lead, documentType) =>
  (Array.isArray(lead.documents) ? lead.documents : [])
    .filter((d) => d.type === documentType)
    .map((d, index) => ({
      id: d.id || d.file || String(index),
      nom: d.name || d.filename || "document",
      // Le nom du fichier choisit l'icône : le libellé affiché peut ne pas porter d'extension (« Devis AssurAli », PER-2500).
      fichier: d.filename || d.nom || d.name,
      deposeLe: d.uploadedAt || d.createdAt,
      ia: d.origine === "agent",
      iaLe: d.genereLe || d.uploadedAt,
      compagnie: d.nomCompagnie || null,
      ouvrir: () => ouvrirDocument(d),
      telecharger: () => telechargerDocument(d),
    }));
